import { t as uiText } from '../i18n';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import {
  PAGE_COUNT_CACHE_MAX_AGE_MS,
  isCurrentRunPageCount,
  isUsablePageCount,
  readPageCount,
  type ChildPageCountResult,
} from './agentPageCountData';

export { PAGE_COUNT_CACHE_MAX_AGE_MS, isCurrentRunPageCount, isUsablePageCount, readPageCount };
export type { ChildPageCountResult };

export const PAGE_COUNT_TIMEOUT_MS = 120_000;

export async function sha256Hex16(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .substring(0, 16);
}

export function pageCountHashInput(baseUrl: string, includeSubdomains = true, useHeadless = false): string {
  return `${baseUrl}|subdomains:${includeSubdomains ? '1' : '0'}|headless:${useHeadless ? '1' : '0'}`;
}

/** Start crawler-service page count and wait for the Firestore result. */
export async function countChildPages(
  workspaceId: string,
  baseUrl: string,
  timeoutMs: number = PAGE_COUNT_TIMEOUT_MS
): Promise<ChildPageCountResult> {
  const includeSubdomains = true;
  const useHeadless = false;
  const hash = await sha256Hex16(pageCountHashInput(baseUrl, includeSubdomains, useHeadless));
  const ref = doc(db, 'publicPages', workspaceId, 'settings', 'crawler', 'crawlPageCounts', hash);

  const cached = await getDoc(ref);
  if (cached.exists() && isUsablePageCount(cached.data() as Record<string, unknown>)) {
    return readPageCount(cached.data() as Record<string, unknown>);
  }

  const start = httpsCallable(functions, 'startCrawlerPageCountFn', { timeout: 180_000 });
  const startedAt = Date.now();
  await start({
    workspaceId,
    baseUrl,
    includeSubdomains,
    useHeadless,
    force: false,
  });

  const acceptThisRun = (data: Record<string, unknown> | undefined) => {
    if (!data) return false;
    return isUsablePageCount(data) || isCurrentRunPageCount(data, startedAt);
  };

  const afterStart = await getDoc(ref);
  if (afterStart.exists() && acceptThisRun(afterStart.data() as Record<string, unknown>)) {
    return readPageCount(afterStart.data() as Record<string, unknown>);
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.clearInterval(poll);
      unsub();
      fn();
    };

    const takeIfUsable = (data: Record<string, unknown> | undefined) => {
      if (!acceptThisRun(data)) return false;
      finish(() => resolve(readPageCount(data as Record<string, unknown>)));
      return true;
    };

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        takeIfUsable(snap.data() as Record<string, unknown>);
      },
      (err) => finish(() => reject(err))
    );

    const poll = window.setInterval(async () => {
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) {
          takeIfUsable(snap.data() as Record<string, unknown>);
        }
      } catch {
        // keep waiting until timeout
      }
    }, 3_000);

    const timer = window.setTimeout(async () => {
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) {
          finish(() => resolve(readPageCount(snap.data() as Record<string, unknown>)));
          return;
        }
      } catch {
        // fall through
      }
      finish(() => reject(new Error(uiText("Counting child pages timed out"))));
    }, timeoutMs);
  });
}
